export type LandingCard = {
  title: string
  icon?: string
  link: string
  linkText?: string
  description?: string
  target?: string
  managerOnly?: boolean
  // Scoped admin role that unlocks this card for non-full-admin users.
  // Omitted for cards common to every admin-type role (isAdmin/isAPAdmin/isFEAdmin/isRPAdmin).
  requiredRole?: 'isAPAdmin' | 'isFEAdmin' | 'isRPAdmin'
}

/** Role flags used to decide which admin landing cards are visible. */
export interface AdminCardRoles {
  isAdmin?: boolean
  isAPAdmin?: boolean
  isFEAdmin?: boolean
  isRPAdmin?: boolean
}

export const adminLandingCards: LandingCard[] = [
  {
    title: 'Assign Approver Proxy',
    description:
      'Assign Approver proxies to allow proxies to process requests.',
    icon: '/assets/images/assign-approver-proxy.svg',
    link: '/admin/approver-proxy',
    linkText: 'Find and Select',
    requiredRole: 'isAPAdmin',
  },
  {
    title: 'Assign Float Employee',
    description:
      "Assign a Float Employee access to an additional Approver Group's calendar view.",
    icon: '/assets/images/assign-floar-emp.svg',
    link: '/admin/float-proxy',
    linkText: 'Find and Select',
    requiredRole: 'isFEAdmin',
  },
  {
    title: 'Assign Requestor Proxy',
    description:
      'Assign Requestor Proxies to allow proxies to make time off requests on behalf of other employees.',
    icon: '/assets/images/assign-requestor-proxy.svg',
    link: '/admin/requestor-proxy',
    linkText: 'Find and Select',
    requiredRole: 'isRPAdmin',
  },
  {
    title: 'Manager Process Time Off',
    description: 'Process incoming time off requests.',
    icon: '/assets/images/approve-time-off.svg',
    link: '/approver',
    linkText: 'Go to TORT',
  },
  {
    title: 'Employee Request Time Off',
    description: 'Request time off or check on a status.',
    icon: '/assets/images/request-time-off.svg',
    link: '/requestor',
    linkText: 'Go to TORT',
  },
  {
    title: 'Manual Employee Time Off Search',
    description: 'Request time off or check on a request status.',
    icon: '/assets/images/manual-emp-timeoff.svg',
    link: '/admin/manual-timeoff',
    linkText: 'Find and Select',
  },
]

/**
 * Filters {@link adminLandingCards} down to the cards a user is allowed to
 * see, preserving the fixed render order (Approver Proxy, Float Employee,
 * Requestor Proxy, then the common cards). Cards without a `requiredRole`
 * are shown to any admin-type user (full admin or any scoped sub-admin).
 * Full admins (`isAdmin`) always see every card.
 */
export const getVisibleAdminCards = (roles: AdminCardRoles): LandingCard[] => {
  const isAnyAdmin = Boolean(
    roles.isAdmin || roles.isAPAdmin || roles.isFEAdmin || roles.isRPAdmin
  )

  return adminLandingCards.filter((card) => {
    if (!card.requiredRole) return isAnyAdmin
    return Boolean(roles.isAdmin || roles[card.requiredRole])
  })
}

/**
 * Splits the cards visible to `roles` into the flag-driven assignment cards
 * (Approver/Float/Requestor Proxy) and the fixed common cards (Manager
 * Process Time Off, Employee Request Time Off, Manual Employee Time Off
 * Search). Rendering these as two separate card groups keeps the common
 * cards pinned to their own row, regardless of how many assignment cards a
 * given flag combination unlocks, while the assignment group still reflows
 * dynamically with no gaps.
 */
export const getAdminCardGroups = (
  roles: AdminCardRoles
): { assignmentCards: LandingCard[]; commonCards: LandingCard[] } => {
  const visibleCards = getVisibleAdminCards(roles)
  return {
    assignmentCards: visibleCards.filter((card) => card.requiredRole),
    commonCards: visibleCards.filter((card) => !card.requiredRole),
  }
}


export default adminLandingCards
